import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Plus, Minus, Truck, PackageCheck, Trash2, Printer, Pencil, Search } from "lucide-react";
import api, { formatError, API } from "@/lib/api";
import { useStore } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { fmtEuro, fmtDate } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Inventory() {
  const { stores, selectedStore } = useStore();
  const { can } = useAuth();
  const [parts, setParts] = useState([]);
  const [moves, setMoves] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [query, setQuery] = useState("");
  const [supOpen, setSupOpen] = useState(false);
  const [ordOpen, setOrdOpen] = useState(false);
  const [f, setF] = useState({ sku: "", name: "", brand: "", category: "", compatibility: "", purchase_price: 0, sale_price: 0, minimum_stock: 1, quantity: 0, store_id: "" });
  const [sup, setSup] = useState({ name: "", vat_number: "", contact: "", phone: "", email: "" });
  const [ord, setOrd] = useState({ supplier_id: "", store_id: "", items: [{ description: "", quantity: 1, unit_price: 0, part_id: null }] });

  const load = () => {
    api.get("/parts", { params: { store_id: selectedStore } }).then((r) => setParts(r.data));
    api.get("/stock/movements").then((r) => setMoves(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/purchase-orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [selectedStore]);
  useEffect(() => {
    if (stores.length) {
      setF((s) => (s.store_id ? s : { ...s, store_id: stores[0].id }));
      setOrd((o) => (o.store_id ? o : { ...o, store_id: stores[0].id }));
    }
  }, [stores]);

  const createPart = async () => {
    try {
      const payload = {
        sku: f.sku, name: f.name, brand: f.brand, category: f.category, compatibility: f.compatibility,
        purchase_price: Number(f.purchase_price), sale_price: Number(f.sale_price),
        minimum_stock: Number(f.minimum_stock), store_id: f.store_id,
      };
      if (editingId) {
        await api.put(`/parts/${editingId}`, payload);
        toast.success("Articolo aggiornato");
      } else {
        await api.post("/parts", { ...payload, quantity: Number(f.quantity) });
        toast.success("Articolo aggiunto");
      }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const emptyPart = { sku: "", name: "", brand: "", category: "", compatibility: "", purchase_price: 0, sale_price: 0, minimum_stock: 1, quantity: 0, store_id: stores[0]?.id || "" };
  const openNew = () => { setEditingId(null); setF(emptyPart); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setF({ sku: p.sku || "", name: p.name || "", brand: p.brand || "", category: p.category || "",
           compatibility: p.compatibility || "", purchase_price: p.purchase_price ?? 0,
           sale_price: p.sale_price ?? 0, minimum_stock: p.minimum_stock ?? 1,
           quantity: p.quantity ?? 0, store_id: p.store_id || "" });
    setOpen(true);
  };
  const deletePart = async (p) => {
    if (!window.confirm(`Eliminare l'articolo "${p.name}"? L'operazione è irreversibile.`)) return;
    try { await api.delete(`/parts/${p.id}`); toast.success("Articolo eliminato"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const adjust = async (part, delta) => {
    await api.post("/stock/movements", { part_id: part.id, store_id: part.store_id, type: delta > 0 ? "PURCHASE" : "ADJUSTMENT", quantity: delta, reason: "Rettifica manuale" });
    load();
  };
  const createSupplier = async () => {
    try { await api.post("/suppliers", sup); toast.success("Fornitore aggiunto"); setSupOpen(false); setSup({ name: "", vat_number: "", contact: "", phone: "", email: "" }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const createOrder = async () => {
    try {
      await api.post("/purchase-orders", { ...ord, items: ord.items.map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })) });
      toast.success("Ordine creato"); setOrdOpen(false);
      setOrd({ supplier_id: "", store_id: stores[0]?.id || "", items: [{ description: "", quantity: 1, unit_price: 0, part_id: null }] }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const receiveOrder = async (oid) => {
    try { await api.patch(`/purchase-orders/${oid}/receive`); toast.success("Merce ricevuta, giacenza aggiornata"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const filteredParts = parts.filter((p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [p.sku, p.name, p.brand, p.category, p.compatibility]
      .some((v) => (v || "").toString().toLowerCase().includes(q));
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Magazzino</h1>
      </div>
      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items" data-testid="inv-tab-items">Articoli</TabsTrigger>
          <TabsTrigger value="moves" data-testid="inv-tab-moves">Movimenti</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="inv-tab-suppliers">Fornitori</TabsTrigger>
          <TabsTrigger value="orders" data-testid="inv-tab-orders">Ordini</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-4 pt-4">
          <div className="flex flex-wrap items-center gap-3">
            {can("inventory.manage") && <Button data-testid="new-part-btn" onClick={openNew}>Nuovo articolo</Button>}
            <div className="relative min-w-[240px] flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="inv-search" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca per SKU, nome, marca, compatibilità…" className="pl-9" />
            </div>
          </div>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-4 py-3">SKU</th><th className="px-4 py-3">Articolo</th><th className="px-4 py-3 hidden md:table-cell">Prezzo</th><th className="px-4 py-3">Giacenza</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredParts.map((p) => (
                  <tr key={p.id} data-testid={`part-row-${p.sku}`} className={p.low_stock ? "bg-rose-50/50 dark:bg-rose-950/20" : ""}>
                    <td className="px-4 py-3 font-mono">{p.sku}</td>
                    <td className="px-4 py-3">{p.name} {p.low_stock && <AlertTriangle className="ml-1 inline h-4 w-4 text-rose-500" />}</td>
                    <td className="px-4 py-3 hidden md:table-cell">{fmtEuro(p.sale_price)}</td>
                    <td className="px-4 py-3 font-semibold">{p.quantity}</td>
                    <td className="px-4 py-3">
                      {can("inventory.manage") && (
                        <div className="flex gap-1">
                          <Button size="icon" variant="outline" className="h-7 w-7" title="Aggiungi giacenza" onClick={() => adjust(p, 1)}><Plus className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="outline" className="h-7 w-7" title="Riduci giacenza" onClick={() => adjust(p, -1)}><Minus className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="outline" className="h-7 w-7" data-testid={`edit-part-${p.sku}`} title="Modifica" onClick={() => openEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="outline" className="h-7 w-7 text-rose-600 hover:text-rose-700" data-testid={`delete-part-${p.sku}`} title="Elimina" onClick={() => deletePart(p)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredParts.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">{query ? "Nessun articolo corrisponde alla ricerca." : "Nessun articolo."}</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="moves" className="pt-4">
          <Card className="overflow-hidden">
            <div className="divide-y divide-border">
              {moves.map((m) => (
                <div key={m.id} className="flex items-center gap-4 px-5 py-2.5 text-sm">
                  <span className={`w-12 font-semibold ${m.quantity < 0 ? "text-rose-600" : "text-emerald-600"}`}>{m.quantity > 0 ? "+" : ""}{m.quantity}</span>
                  <span className="flex-1">{m.part_name}</span>
                  <span className="text-xs text-muted-foreground">{m.type}</span>
                </div>
              ))}
              {moves.length === 0 && <div className="px-5 py-12 text-center text-muted-foreground">Nessun movimento.</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4 pt-4">
          {can("inventory.manage") && <Button data-testid="new-supplier-btn" onClick={() => setSupOpen(true)}>Nuovo fornitore</Button>}
          <Card className="overflow-hidden">
            <div className="divide-y divide-border">
              {suppliers.map((s) => (
                <div key={s.id} data-testid={`supplier-row-${s.id}`} className="flex items-center gap-4 px-5 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Truck className="h-4 w-4" /></span>
                  <div className="flex-1"><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.phone} {s.email && `· ${s.email}`}</div></div>
                  {s.vat_number && <span className="font-mono text-xs text-muted-foreground">{s.vat_number}</span>}
                </div>
              ))}
              {suppliers.length === 0 && <div className="px-5 py-12 text-center text-muted-foreground">Nessun fornitore.</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="orders" className="space-y-4 pt-4">
          {can("inventory.manage") && <Button data-testid="new-order-btn" onClick={() => setOrdOpen(true)}>Nuovo ordine</Button>}
          <div className="space-y-3">
            {orders.map((o) => (
              <Card key={o.id} data-testid={`order-${o.order_number}`} className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono font-semibold text-primary">{o.order_number}</span>
                    <span className="ml-2 text-sm text-muted-foreground">{o.supplier_name}</span>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${o.status === "RECEIVED" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"}`}>
                    {o.status === "RECEIVED" ? "Ricevuto" : "Ordinato"}
                  </span>
                </div>
                <div className="mt-2 text-sm text-muted-foreground">{o.items.map((i) => `${i.description} ×${i.quantity}`).join(", ")}</div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-heading font-bold">{fmtEuro(o.total)}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`print-order-${o.order_number}`} onClick={() => window.open(`${API}/purchase-orders/${o.id}/pdf`, "_blank")}>
                      <Printer className="mr-1 h-4 w-4" /> PDF
                    </Button>
                    {can("inventory.manage") && o.status !== "RECEIVED" && (
                      <Button size="sm" data-testid={`receive-order-${o.order_number}`} onClick={() => receiveOrder(o.id)}>
                        <PackageCheck className="mr-1 h-4 w-4" /> Segna ricevuto
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            {orders.length === 0 && <Card className="p-12 text-center text-muted-foreground">Nessun ordine.</Card>}
          </div>
        </TabsContent>
      </Tabs>

      {/* New / edit part dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label>SKU *</Label><Input data-testid="part-sku" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} /></div>
            <div className="space-y-1"><Label>Nome *</Label><Input data-testid="part-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Marca</Label><Input data-testid="part-brand" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} /></div>
            <div className="space-y-1"><Label>Compatibilità</Label><Input data-testid="part-compatibility" value={f.compatibility} onChange={(e) => setF({ ...f, compatibility: e.target.value })} /></div>
            <div className="space-y-1"><Label>Costo €</Label><Input type="number" data-testid="part-purchase-price" value={f.purchase_price} onChange={(e) => setF({ ...f, purchase_price: e.target.value })} /></div>
            <div className="space-y-1"><Label>Prezzo vendita €</Label><Input type="number" data-testid="part-sale-price" value={f.sale_price} onChange={(e) => setF({ ...f, sale_price: e.target.value })} /></div>
            <div className="space-y-1"><Label>Scorta minima</Label><Input type="number" data-testid="part-minimum-stock" value={f.minimum_stock} onChange={(e) => setF({ ...f, minimum_stock: e.target.value })} /></div>
            {!editingId && <div className="space-y-1"><Label>Quantità iniziale</Label><Input type="number" data-testid="part-quantity" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} /></div>}
            <div className="space-y-1 sm:col-span-2"><Label>Negozio</Label>
              <Select value={f.store_id} onValueChange={(v) => setF({ ...f, store_id: v })}>
                <SelectTrigger data-testid="part-store"><SelectValue /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {editingId && <p className="text-xs text-muted-foreground">La giacenza si modifica dai pulsanti +/- o dai movimenti, non da qui.</p>}
          <DialogFooter><Button data-testid="save-part-btn" onClick={createPart}>{editingId ? "Aggiorna" : "Salva"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New supplier dialog */}
      <Dialog open={supOpen} onOpenChange={setSupOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo fornitore</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2"><Label>Ragione sociale *</Label><Input data-testid="supplier-name" value={sup.name} onChange={(e) => setSup({ ...sup, name: e.target.value })} /></div>
            <div className="space-y-1"><Label>P.IVA</Label><Input value={sup.vat_number} onChange={(e) => setSup({ ...sup, vat_number: e.target.value })} /></div>
            <div className="space-y-1"><Label>Referente</Label><Input value={sup.contact} onChange={(e) => setSup({ ...sup, contact: e.target.value })} /></div>
            <div className="space-y-1"><Label>Telefono</Label><Input value={sup.phone} onChange={(e) => setSup({ ...sup, phone: e.target.value })} /></div>
            <div className="space-y-1"><Label>Email</Label><Input value={sup.email} onChange={(e) => setSup({ ...sup, email: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="save-supplier-btn" onClick={createSupplier}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New order dialog */}
      <Dialog open={ordOpen} onOpenChange={setOrdOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuovo ordine fornitore</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Fornitore</Label>
                <Select value={ord.supplier_id} onValueChange={(v) => setOrd({ ...ord, supplier_id: v })}>
                  <SelectTrigger data-testid="order-supplier"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>Negozio</Label>
                <Select value={ord.store_id} onValueChange={(v) => setOrd({ ...ord, store_id: v })}>
                  <SelectTrigger data-testid="order-store"><SelectValue /></SelectTrigger>
                  <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            {ord.items.map((it, i) => (
              <div key={i} className="flex gap-2">
                <Select value={it.part_id || "none"} onValueChange={(v) => {
                  const p = parts.find((x) => x.id === v);
                  setOrd({ ...ord, items: ord.items.map((x, j) => j === i ? { ...x, part_id: v === "none" ? null : v, description: p ? p.name : x.description, unit_price: p ? p.purchase_price : x.unit_price } : x) });
                }}>
                  <SelectTrigger className="w-40" data-testid={`order-item-part-${i}`}><SelectValue placeholder="Articolo" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Libero</SelectItem>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Descrizione" value={it.description} onChange={(e) => setOrd({ ...ord, items: ord.items.map((x, j) => j === i ? { ...x, description: e.target.value } : x) })} />
                <Input type="number" className="w-16" value={it.quantity} onChange={(e) => setOrd({ ...ord, items: ord.items.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x) })} />
                <Input type="number" className="w-20" placeholder="€" value={it.unit_price} onChange={(e) => setOrd({ ...ord, items: ord.items.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x) })} />
                <Button variant="ghost" size="icon" onClick={() => setOrd({ ...ord, items: ord.items.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="add-order-item" onClick={() => setOrd({ ...ord, items: [...ord.items, { description: "", quantity: 1, unit_price: 0, part_id: null }] })}>
              <Plus className="mr-1 h-4 w-4" /> Riga
            </Button>
          </div>
          <DialogFooter><Button data-testid="save-order-btn" onClick={createOrder}>Crea ordine</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
